import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";

import { BrandForm } from "./brand-form";

import { FormError, FormModal } from "@/components/kit";
import { BRAND_FORM_SCHEMA, BrandFormData } from "@/schemas/brand-form.schema";
import { useCreateBrand } from "@/hooks/api/brand/useCreateBrand";

interface AddBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EMPTY: BrandFormData = {
  id: "",
  image: "",
  name: "",
  nameAr: "",
  isActive: true,
  isLocal: false,
};

export function AddBrandModal({
  isOpen,
  onClose,
  onSuccess,
}: AddBrandModalProps) {
  const form = useForm({
    resolver: zodResolver(BRAND_FORM_SCHEMA),
    mode: "all",
    defaultValues: EMPTY,
  });

  const {
    mutate: createBrand,
    isPending: isCreatingBrand,
    error,
    isError,
    reset: resetMutation,
  } = useCreateBrand();

  const errorMessage = isError
    ? isAxiosError(error)
      ? error.response?.data?.message
      : "An error occured while creating the brand, please try again!"
    : null;

  const handleSubmit = (data: BrandFormData) => {
    // "Leave empty to auto-generate": an empty id is left out of the request instead of sent as "".
    const { id, ...rest } = data;

    createBrand(id ? data : rest, {
      onSuccess: () => {
        form.reset(EMPTY);
        onSuccess();
      },
    });
  };

  const close = () => {
    resetMutation();
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitting={isCreatingBrand}
      submitLabel="Add brand"
      title="Add brand"
      onClose={close}
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <FormProvider {...form}>
        <FormError message={errorMessage} />
        <BrandForm />
      </FormProvider>
    </FormModal>
  );
}
